/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';

import { I18n } from '@trebox-arch/i18n';

// Locale strings for the login page. Lives inside the page rather than the
// global studio-i18n-resource bag because (a) the keys are trebox-specific
// and the legacy coze resource is mid-migration, and (b) the count is small
// enough that a colocated table is easier to audit than 30 new keys spread
// across en.json + zh-CN.json + locale-data.d.ts.
const STRINGS = {
  zh: {
    brand_badge: '企业级多租户 AI Agent 平台',
    hero_line_1: '为你的团队',
    hero_line_2: '构建可自我进化的',
    hero_line_3: '数字员工。',
    hero_subtitle:
      '对话、工具、知识、记忆、编排 —— 八个能力域按需组合。 业务用户与开发者共享同一份资源树。',
    footer_copy: '© 2026 Trebox Inc.',
    footer_privacy: '隐私政策',
    footer_tos: '服务条款',
    footer_status: '状态',
    card_chat: '对话',
    card_chat_body: '@营销文案 v3',
    card_agent: 'Agent',
    card_agent_body: '新版本 v4 已发布',
    card_skill: 'Skill',
    card_skill_body: 'GB/T 1.1-2020 合规',
    signup_prompt: '还没有账号?',
    signup_link: '申请试用 →',
    title: '登录 Trebox',
    subtitle: '使用邮箱 / 手机号 / 用户名继续,或选择企业身份提供商。',
    label_identifier: '账号',
    placeholder_identifier: '邮箱 / 手机号 / 用户名',
    label_password: '密码',
    forgot_password: '忘记密码?',
    remember_30d: '保持登录 30 天',
    submit: '登录 →',
    submitting: '登录中…',
    oidc_divider: '或继续使用',
    tos_full:
      '登录即表示同意 {{tos}} 与 {{privacy}}。 企业租户启用 SSO 后将由管理员引导授权。',
    tos_link: '服务条款',
    privacy_link: '隐私政策',
  },
  en: {
    brand_badge: 'Enterprise multi-tenant AI Agent platform',
    hero_line_1: 'Build self-evolving',
    hero_line_2: 'digital employees',
    hero_line_3: 'for your team.',
    hero_subtitle:
      'Conversations, tools, knowledge, memory, orchestration — eight capability domains, composable on demand. Business users and developers share one resource tree.',
    footer_copy: '© 2026 Trebox Inc.',
    footer_privacy: 'Privacy',
    footer_tos: 'Terms',
    footer_status: 'Status',
    card_chat: 'Chat',
    card_chat_body: '@marketing-copy v3',
    card_agent: 'Agent',
    card_agent_body: 'v4 released',
    card_skill: 'Skill',
    card_skill_body: 'GB/T 1.1-2020 compliant',
    signup_prompt: "Don't have an account?",
    signup_link: 'Request trial →',
    title: 'Sign in to Trebox',
    subtitle:
      'Continue with email / phone / username, or choose your enterprise identity provider.',
    label_identifier: 'Account',
    placeholder_identifier: 'Email / phone / username',
    label_password: 'Password',
    forgot_password: 'Forgot?',
    remember_30d: 'Stay signed in for 30 days',
    submit: 'Sign in →',
    submitting: 'Signing in…',
    oidc_divider: 'Or continue with',
    tos_full:
      'By signing in you agree to the {{tos}} and {{privacy}}. Enterprise tenants will be guided through SSO by their admin.',
    tos_link: 'Terms of Service',
    privacy_link: 'Privacy Policy',
  },
} as const;

export type LoginStrings = (typeof STRINGS)['zh'];

const detectLang = (raw: string | undefined): 'zh' | 'en' =>
  raw && raw.toLowerCase().startsWith('en') ? 'en' : 'zh';

export const useLoginI18n = (): LoginStrings => {
  const [lang, setLang] = useState<'zh' | 'en'>(() =>
    detectLang(I18n.language),
  );

  useEffect(() => {
    const inst = I18n.i18nInstance as
      | {
          on: (evt: 'languageChanged', cb: (l: string) => void) => void;
          off: (evt: 'languageChanged', cb: (l: string) => void) => void;
        }
      | undefined;
    if (!inst?.on) {
      return;
    }
    const cb = (l: string) => setLang(detectLang(l));
    inst.on('languageChanged', cb);
    return () => inst.off('languageChanged', cb);
  }, []);

  return STRINGS[lang];
};
